import * as Yup from "yup";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { Formik } from "formik";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import theme from "styles/theme";
import { UserInfo } from "pages/MeusDados";

import { Container, FormContainer, Text, TokenLink } from "./styles";
import { ContainerButtons, ModalHeader, ModalStyle, Title } from "../styles";

type TokenPhoneProps = {
  openModal: boolean;
  modalFunction: Dispatch<SetStateAction<boolean>>;
  userInfo: UserInfo;
  setUserInfo: Dispatch<SetStateAction<UserInfo>>;
  phoneAwait: string;
};

const TokenPhone = ({
  openModal,
  modalFunction,
  userInfo,
  setUserInfo,
  phoneAwait,
}: TokenPhoneProps): JSX.Element => {
  const [invalidToken, setInvalidToken] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>();
  const [userContact, setUserContact] = useState();

  const validationSchema = (): any =>
    Yup.object().shape({
      token: Yup.number("O token é composto somente por números").required(
        "Insira um código"
      ),
    });

  const requestGetContactId = useCallback(
    async (headers) => {
      const { document, userId, customerCode } = userInfo;
      const params = {
        document,
        contactType: "PHONE",
        customerCode,
        contact: phoneAwait.replace(/[^\d]/g, ""),
        "is-contact-validated": false,
        _limit: 1,
        _offset: 0,
      };
      const response = await api.get(
        `industrial/customer/v1/users/${userId}/contacts`,
        { headers, params }
      );
      const { data } = response;
      setUserContact(data[0]);
      return data[0];
    },
    [api, phoneAwait, setUserContact]
  );

  const requestSendSmsToken = useCallback(
    async (responseContactId, headers) => {
      const { contactId } = responseContactId;
      const { customerCode } = userInfo;
      const { userId } = userInfo;
      const document = localStorage.getItem("@UltragazEmpresarial:document");
      const body = {
        tokenType: "REGISTER",
        url: "",
        contactId,
        customerCode,
        document,
      };
      await api.post(`industrial/customer/v1/users/${userId}/tokens`, body, {
        headers,
      });
      toast.success("Token enviado com sucesso");
    },
    [userInfo]
  );

  const resendSmsToken = useCallback(async () => {
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    try {
      if (userInfo) {
        let responseContactId = userContact;
        toast.dismiss();
        if (!responseContactId) {
          responseContactId = await requestGetContactId(headers);
        }
        await requestSendSmsToken(responseContactId, headers);
      }
    } catch (err) {
      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  }, [userInfo, userContact, toast, requestGetContactId, requestSendSmsToken]);

  const requestTokenValidation = useCallback(
    async (token) => {
      toast.dismiss();
      try {
        setLoading(true);
        const { userId } = userInfo;
        const headers = {
          access_token: localStorage.getItem(
            "@UltragazEmpresarial:accessToken"
          ),
        };
        const body = {
          token,
        };
        await api.post(
          `industrial/customer/v1/users/${userId}/validations`,
          body,
          {
            headers,
          }
        );
        const phone = phoneAwait.replace(/[^\d]/g, "");
        const phoneDdd = phone.slice(0, 2);
        const phoneNumber = phone.slice(2);

        setUserInfo({ ...userInfo, ...{ phoneDdd, phoneNumber } });
        setLoading(false);
        modalFunction(false);
      } catch (err) {
        setLoading(false);
        setInvalidToken(true);
        const erro = err.response;
        if (erro) {
          const { message } = err.response.data;
          if (err.response.status !== 401) {
            toast.error(message);
          }
        } else if (err.response.status !== 401) {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      }
    },
    [api, setLoading, modalFunction, userInfo, setUserInfo, phoneAwait]
  );

  const handleSubmit = useCallback(
    ({ token }) => {
      if (!loading) {
        requestTokenValidation(token);
      }
    },
    [requestTokenValidation, loading]
  );

  return (
    <ModalStyle
      show={openModal}
      animation={false}
      centered
      onHide={() => modalFunction(false)}
    >
      <ModalHeader>
        <span>Editar dados</span>
        <MdClose size={24} onClick={() => modalFunction(false)} />
      </ModalHeader>
      <Container>
        <Title>Telefone de contato</Title>
        <Text>Insira o token enviado por SMS para o celular {phoneAwait}</Text>
        <Formik
          initialValues={{
            token: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          render={({ handleChange, handleBlur, values, errors, touched }) => (
            <FormContainer>
              <TextField
                id="token"
                name="token"
                type="text"
                label="Código de validação"
                inputMode="numeric"
                // @ts-ignore
                errorCondition={errors.token && touched.token}
                // @ts-ignore
                errorMessage={errors.token}
                onBlur={handleBlur}
                placeholder="Insira o código de validação"
                value={values.token}
                onChange={handleChange}
                onClick={() => toast.dismiss()}
              />
              <TokenLink
                invalidToken={invalidToken}
                onClick={() => resendSmsToken()}
              >
                {invalidToken ? "Token inválido, " : "Não recebeu o código? "}
                Clique aqui para reenviar
              </TokenLink>
              <ContainerButtons>
                <OutlinedButton
                  color={theme.colors.purple}
                  onClick={() => modalFunction(false)}
                >
                  Cancelar
                </OutlinedButton>
                <ContainedButton type="submit" loading={loading}>
                  Atualizar
                </ContainedButton>
              </ContainerButtons>
            </FormContainer>
          )}
        />
      </Container>
    </ModalStyle>
  );
};

export default TokenPhone;
